import { useEffect } from 'react';
import {
  ActionItemList,
  ActionItemListSkeleton,
  BodyTextLarge,
  Button,
  Grid,
  GridItem,
  HeadingText40,
} from '@modernhealth/vital-components';
import { captureException as sentryCaptureException } from '@sentry/minimal';
import { AnalyticsEventType, useAnalytics } from '@modernhealth/web-analytics';
import { FormattedMessage } from 'react-intl';

import { useFetchSeriesQuery } from 'src/modules/circles/documents/series.generated';
import { captureException } from 'src/shared/utils/captureException';

const RowContainer: React.FC = ({ children }) => (
  <section>
    <HeadingText40 marginTop={64} marginBottom={32}>
      <FormattedMessage defaultMessage="Explore series" />
    </HeadingText40>
    {children}
  </section>
);

export const ExploreSeriesRow: React.FC = () => {
  const analytics = useAnalytics();
  const { data, loading, refetch } = useFetchSeriesQuery();

  const { items } = data?.circleSeriesCollection ?? {};

  useEffect(() => {
    if (loading) return;

    if (!items || items.length === 0) {
      sentryCaptureException(new Error('Circles failed to load'));
      captureException(new Error('Circles failed to load'));
    }
  }, [loading, items]);

  if (loading && !data) {
    return (
      <RowContainer>
        <Grid lgMode="3columns">
          <GridItem lg={1} md={2}>
            <ActionItemListSkeleton actions={[{}, {}, {}]} showSecondaryButton />
          </GridItem>
          <GridItem lg={1} md={2} hide="sm">
            <ActionItemListSkeleton actions={[{}, {}, {}]} showSecondaryButton />
          </GridItem>
          <GridItem lg={1} md={2} hide="md">
            <ActionItemListSkeleton actions={[{}, {}, {}]} showSecondaryButton />
          </GridItem>
        </Grid>
      </RowContainer>
    );
  }

  if (!items || items.length === 0) {
    return (
      <RowContainer>
        <div
          css={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyItems: 'center',
          }}
        >
          <BodyTextLarge css={{ marginBottom: 16 }}>Failed to load Circles</BodyTextLarge>
          <Button
            title="Try again"
            variant="primary"
            size="medium"
            onClick={() => {
              refetch();

              analytics.track('Circles: Explore Series Failed to Load', AnalyticsEventType.ERROR);
              analytics.trackClick('Circles: Reload Explore Series');

              captureException(new Error('Circle series were refetched due to load failure.'));
            }}
          />
        </div>
      </RowContainer>
    );
  }

  return (
    <RowContainer>
      <Grid lgMode="3columns">
        {items.flatMap(item => {
          if (!item) return [];

          return (
            <GridItem lg={1} md={2} column key={`actionItemList_column_${item.id}`}>
              <ActionItemList
                css={{
                  // TODO(evanmh): Add line height restrictions to Vita components
                  p: {
                    display: '-webkit-box',
                    lineClamp: 3,
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  },
                }}
                actions={[
                  {
                    id: item.id,
                    title: item.name,
                    description: `${item.description.slice(0, 100).trim()}...`,
                    startIconUrl: item.illustration ?? '',
                    linkTo: `/series/${item.slug}`,
                  },
                ]}
              />
            </GridItem>
          );
        })}
      </Grid>
    </RowContainer>
  );
};
